/**
 * A frame showing a live camera or screen stream from the platform call engine, with overlay content on top (name,
 * mute and hand badges, an avatar when there is no video). `source` is an opaque id the engine hands out
 * (`CallEngineState.localVideo`, `remoteVideo[userId]`, …), resolved through `LiveMediaContext`; without one only the
 * background and the overlay show.
 *
 * Native: iOS `ZStack { RTCMTLVideoView (UIViewRepresentable) ; overlay }` with `.aspectRatio`, `.clipShape` and a
 * stroke for `border`; Android `Box { AndroidView(SurfaceViewRenderer) ; overlay }` with `Modifier.aspectRatio`,
 * `clip` and `border`. Both look the track up in the engine's registry by the same id. Never plays audio.
 */
import { createContext, useContext, useEffect, useRef, useSyncExternalStore, type ReactNode } from "react";
import "./call.css";
import { color, radius as radiusVar, type ColorToken, type RadiusToken } from "./tokens.ts";

/** The platform's live streams by source id (web: the call engine's MediaStream registry). */
export type LiveMedia = {
  get(source: string): MediaStream | undefined;
  subscribe(listener: () => void): () => void;
};

const none: LiveMedia = { get: () => undefined, subscribe: () => () => {} };

export const LiveMediaContext = createContext<LiveMedia>(none);

export type LiveVideoProps = {
  /** Engine source id; absent or unknown shows no video. */
  source?: string;
  /** Flip horizontally (my own camera, when "Mirror my video" is on). */
  mirror?: boolean;
  /** cover: fill and crop (people); contain: letterbox (screens). Default cover. */
  fit?: "cover" | "contain";
  /** Frame shape: width:height, or "fill" = take the space the parent gives (grow). Default 16:9. */
  aspect?: "16:9" | "4:3" | "1:1" | "fill";
  radius?: RadiusToken;
  background?: ColorToken;
  /** A 2-point ring inside the frame (e.g. `status.success` while speaking). */
  border?: ColorToken;
  accessibilityLabel?: string;
  /** Overlay, stretched over the frame (lay it out with Stack: justify/align place badges and names). */
  children?: ReactNode;
  testID?: string;
};

const RATIO = { "16:9": "16 / 9", "4:3": "4 / 3", "1:1": "1 / 1" } as const;

export function LiveVideo({ source, mirror = false, fit = "cover", aspect = "16:9", radius, background, border, accessibilityLabel, children, testID }: LiveVideoProps) {
  const media = useContext(LiveMediaContext);
  const stream = useSyncExternalStore(
    media.subscribe,
    () => (source === undefined ? undefined : media.get(source)),
    () => undefined,
  );
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el === null) return;
    if (el.srcObject !== (stream ?? null)) el.srcObject = stream ?? null;
    if (stream !== undefined) void el.play().catch(() => undefined);
  }, [stream]);
  return (
    <div
      className={`b-live-frame${aspect === "fill" ? " b-live-frame-fill" : ""}`}
      style={{
        aspectRatio: aspect === "fill" ? undefined : RATIO[aspect],
        borderRadius: radius === undefined ? undefined : radiusVar(radius),
        background: background === undefined ? undefined : color(background),
      }}
      role={accessibilityLabel === undefined ? undefined : "img"}
      aria-label={accessibilityLabel}
      data-testid={testID}
    >
      {stream === undefined ? null : (
        <video ref={ref} className={`b-live-video b-live-video-${fit}${mirror ? " b-live-video-mirror" : ""}`} autoPlay playsInline muted />
      )}
      {children === undefined ? null : <div className="b-live-overlay">{children}</div>}
      {border === undefined ? null : <div className="b-live-ring" style={{ boxShadow: `inset 0 0 0 2px ${color(border)}`, borderRadius: radius === undefined ? undefined : radiusVar(radius) }} />}
    </div>
  );
}
